// ./src/components/features/Forms/FormThemeSelector/FormThemeSelector.tsx
import React, { useMemo } from "react";
import { Select, SelectProps, Group, Text, Stack, Box } from "@mantine/core";

import { AppIcon } from "@/components/generic";
import { THEME_OPTIONS, DEFAULT_THEME, ThemeOption } from "@/config";

export interface FormThemeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Currently selected theme value */
  value?: string | null | undefined;

  /** Callback fired when theme selection changes */
  onChange?: (themeValue: string | null, theme?: ThemeOption) => void;

  /** Placeholder text when no theme is selected */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | number | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show description below the selector */
  showDescription?: boolean;

  /** Custom description text */
  description?: string;
}

interface ThemeOptionData {
  value: string;
  label: string;
  description?: string;
  iconName?: string;
}

// Map theme values to icons and descriptions
const THEME_METADATA: Record<
  string,
  { iconName: string; description: string }
> = {
  default: {
    iconName: "Palette",
    description: "Clean, minimal styling included with the form",
  },
  bootstrap5: {
    iconName: "BrandBootstrap",
    description: "Bootstrap 5 compatible classes and structure",
  },
  tailwind: {
    iconName: "BrandTailwind",
    description: "Tailwind CSS utility classes",
  },
  none: {
    iconName: "Code",
    description: "Raw HTML without any styling classes",
  },
};

export const FormThemeSelector: React.FC<FormThemeSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a theme",
  required = false,
  error,
  disabled,
  size = "md",
  showDescription = false,
  description,
  ...selectProps
}) => {
  // Prepare theme options with metadata
  const themeOptions = useMemo((): ThemeOptionData[] => {
    return THEME_OPTIONS.map((theme) => {
      const metadata = THEME_METADATA[theme.value] || {
        iconName: "Palette",
        description: "",
      };

      return {
        value: theme.value,
        label: theme.label,
        description: metadata.description,
        iconName: metadata.iconName,
      };
    });
  }, []);

  // Create a map for quick theme lookup by value
  const themeMap = useMemo(() => {
    const map = new Map<string, ThemeOption>();
    THEME_OPTIONS.forEach((theme) => {
      map.set(theme.value, theme);
    });
    return map;
  }, []);

  // Get metadata for selected theme
  const selectedThemeMetadata = useMemo(() => {
    if (!value) return null;
    return THEME_METADATA[value] || null;
  }, [value]);

  // Handle onChange with theme object lookup
  const handleChange = (selectedThemeValue: string | null) => {
    if (selectedThemeValue) {
      const theme = themeMap.get(selectedThemeValue);
      onChange?.(selectedThemeValue, theme);
    } else {
      // If cleared and required, reset to default
      if (required) {
        const defaultTheme = themeMap.get(DEFAULT_THEME);
        onChange?.(DEFAULT_THEME, defaultTheme);
      } else {
        onChange?.(null, undefined);
      }
    }
  };

  // Render option for dropdown (shows icon, name, description)
  const renderOption: SelectProps["renderOption"] = ({ option }) => {
    const optionData = option as ThemeOptionData;

    return (
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Box pt={2}>
          <AppIcon iconName={optionData.iconName || "Palette"} size={20} />
        </Box>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500}>
            {optionData.label}
          </Text>
          {optionData.description && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {optionData.description}
            </Text>
          )}
        </Stack>
      </Group>
    );
  };

  // Render selected item icon
  const renderSelectedIcon = () => {
    if (!selectedThemeMetadata?.iconName) return undefined;
    return <AppIcon iconName={selectedThemeMetadata.iconName} size={18} />;
  };

  return (
    <Box>
      <Select
        {...selectProps}
        size={size}
        value={value ?? DEFAULT_THEME}
        onChange={handleChange}
        placeholder={placeholder}
        clearable={!required}
        required={required}
        disabled={disabled}
        error={error}
        data={themeOptions}
        leftSection={renderSelectedIcon()}
        renderOption={renderOption}
        description={showDescription ? description : undefined}
      />
    </Box>
  );
};
